// Vue Kanban d'un board : colonnes réordonnables, cartes drag & drop
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Pencil, Check, X, CheckSquare, MessageSquare, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CardDialog } from "@/components/CardDialog";
import { UserAvatar } from "@/components/UserAvatar";
import { PRIORITES } from "@/lib/constants";
import { formatCourt, estEnRetard } from "@/lib/dates";

// ——— Aperçu d'une carte dans la colonne ———
const ApercuCarte = ({ carte, users, onClick }) => {
  const fait = carte.checklist?.filter((c) => c.fait).length || 0;
  return (
    <div
      onClick={onClick}
      className="bg-background rounded-xl border border-border p-3.5 space-y-2 cursor-pointer hover:border-primary/50 transition-colors"
      data-testid={`kanban-card-${carte.id}`}
    >
      {carte.labels?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {carte.labels.map((l, i) => (
            <span key={i} className="text-[10px] font-medium text-white rounded-full px-2 py-0.5" style={{ background: l.couleur }}>
              {l.nom}
            </span>
          ))}
        </div>
      )}
      <p className="text-sm font-medium leading-snug">{carte.titre}</p>
      <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
        {carte.echeance && (
          <span className={estEnRetard(carte.echeance) ? "text-destructive font-semibold" : ""}>{formatCourt(carte.echeance)}</span>
        )}
        {carte.priorite !== "normale" && (
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${PRIORITES[carte.priorite]?.classe}`}>{PRIORITES[carte.priorite]?.label}</span>
        )}
        {carte.checklist?.length > 0 && (
          <span className="flex items-center gap-1"><CheckSquare className="h-3 w-3" /> {fait}/{carte.checklist.length}</span>
        )}
        {carte.commentaires?.length > 0 && (
          <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" /> {carte.commentaires.length}</span>
        )}
        <span className="flex -space-x-1.5 ml-auto">
          {carte.assignes?.map((id) => {
            const u = users.find((x) => x.id === id);
            return u ? <UserAvatar key={id} user={u} size="sm" /> : null;
          })}
        </span>
      </div>
    </div>
  );
};

export default function BoardView() {
  const { boardId } = useParams();
  const { user } = useAuth();
  const canEdit = ["admin", "media"].includes(user.role);
  const [board, setBoard] = useState(null);
  const [columns, setColumns] = useState([]);
  const [cards, setCards] = useState([]);
  const [users, setUsers] = useState([]);
  const [carteOuverte, setCarteOuverte] = useState(null);
  const [ajoutCarte, setAjoutCarte] = useState(null); // { colId, titre }
  const [renommage, setRenommage] = useState(null); // { colId, nom }
  const [ajoutColonne, setAjoutColonne] = useState("");
  const [colonneFormVisible, setColonneFormVisible] = useState(false);

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get(`/boards/${boardId}`);
      setBoard(data.board);
      setColumns(data.columns);
      setCards(data.cards);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [boardId]);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);

  const cartesColonne = (colId) => cards.filter((c) => c.column_id === colId).sort((a, b) => a.ordre - b.ordre);

  const onDragEnd = async (result) => {
    const { destination, source, draggableId, type } = result;
    if (!destination || !canEdit) return;
    if (type === "COLUMN") {
      if (destination.index === source.index) return;
      const nouvelles = Array.from(columns);
      const [dep] = nouvelles.splice(source.index, 1);
      nouvelles.splice(destination.index, 0, dep);
      setColumns(nouvelles);
      try {
        await api.put(`/boards/${boardId}/columns-order`, { ordre: nouvelles.map((c) => c.id) });
      } catch (e) {
        toast.error(apiError(e));
        charger();
      }
      return;
    }
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;
    // Mise à jour optimiste de l'ordre local
    setCards((prev) => {
      const carte = prev.find((c) => c.id === draggableId);
      const cible = prev
        .filter((c) => c.column_id === destination.droppableId && c.id !== draggableId)
        .sort((a, b) => a.ordre - b.ordre);
      cible.splice(destination.index, 0, { ...carte, column_id: destination.droppableId });
      const majs = new Map(cible.map((c, i) => [c.id, { column_id: destination.droppableId, ordre: i }]));
      return prev.map((c) => (majs.has(c.id) ? { ...c, ...majs.get(c.id) } : c));
    });
    try {
      await api.post(`/cards/${draggableId}/move`, { column_id: destination.droppableId, position: destination.index });
    } catch (e) {
      toast.error(apiError(e));
      charger();
    }
  };

  const creerCarte = async (e) => {
    e.preventDefault();
    try {
      await api.post("/cards", { board_id: boardId, column_id: ajoutCarte.colId, titre: ajoutCarte.titre });
      setAjoutCarte(null);
      charger();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const renommerColonne = async () => {
    try {
      await api.patch(`/columns/${renommage.colId}`, { nom: renommage.nom });
      setRenommage(null);
      charger();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const supprimerColonne = async (colId) => {
    if (!window.confirm("Supprimer cette colonne et ses cartes ?")) return;
    try {
      await api.delete(`/columns/${colId}`);
      charger();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const creerColonne = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/boards/${boardId}/columns`, { nom: ajoutColonne });
      setAjoutColonne("");
      setColonneFormVisible(false);
      charger();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const dupliquer = async () => {
    try {
      await api.post(`/boards/${boardId}/duplicate`);
      toast.success("Board dupliqué — retrouvez la copie dans Projets");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!board) return <div className="py-24 text-center text-muted-foreground">Chargement du board…</div>;

  return (
    <div className="space-y-6" data-testid="board-view-page">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/projets" className="p-2 rounded-full hover:bg-muted transition-colors" data-testid="board-back-link">
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div className="flex-1 min-w-0">
          <h1 className="font-serif text-3xl md:text-4xl font-bold truncate">{board.nom}</h1>
          {board.description && <p className="text-sm text-muted-foreground truncate">{board.description}</p>}
        </div>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={dupliquer} className="rounded-full gap-1.5" data-testid="board-duplicate-button">
            <Copy className="h-3.5 w-3.5" /> Dupliquer comme template
          </Button>
        )}
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="board" direction="horizontal" type="COLUMN">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps} className="flex gap-4 overflow-x-auto pb-4 kanban-scroll items-start" data-testid="kanban-board">
              {columns.map((col, index) => (
                <Draggable key={col.id} draggableId={col.id} index={index} isDragDisabled={!canEdit}>
                  {(colProvided) => (
                    <div
                      ref={colProvided.innerRef}
                      {...colProvided.draggableProps}
                      className="w-72 shrink-0 bg-card rounded-2xl border border-border p-3"
                      data-testid={`kanban-column-${col.nom.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <div className="flex items-center gap-2 px-1 pb-3" {...colProvided.dragHandleProps}>
                        {renommage?.colId === col.id ? (
                          <span className="flex items-center gap-1 flex-1">
                            <Input value={renommage.nom} onChange={(e) => setRenommage({ ...renommage, nom: e.target.value })} className="h-7 rounded-lg text-sm" autoFocus data-testid="column-rename-input" />
                            <button onClick={renommerColonne} className="p-1 text-success" data-testid="column-rename-save"><Check className="h-4 w-4" /></button>
                            <button onClick={() => setRenommage(null)} className="p-1 text-muted-foreground"><X className="h-4 w-4" /></button>
                          </span>
                        ) : (
                          <>
                            <h3 className="font-semibold text-sm flex-1">{col.nom}</h3>
                            <span className="text-xs text-muted-foreground rounded-full bg-muted px-2 py-0.5">{cartesColonne(col.id).length}</span>
                            {canEdit && (
                              <>
                                <button onClick={() => setRenommage({ colId: col.id, nom: col.nom })} className="p-1 text-muted-foreground hover:text-foreground transition-colors" data-testid={`column-rename-${col.id}`}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => supprimerColonne(col.id)} className="p-1 text-muted-foreground hover:text-destructive transition-colors" data-testid={`column-delete-${col.id}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                      <Droppable droppableId={col.id} type="CARD">
                        {(dropProvided, snapshot) => (
                          <div
                            ref={dropProvided.innerRef}
                            {...dropProvided.droppableProps}
                            className={`space-y-2 min-h-[40px] rounded-xl transition-colors ${snapshot.isDraggingOver ? "bg-accent/10" : ""}`}
                          >
                            {cartesColonne(col.id).map((carte, i) => (
                              <Draggable key={carte.id} draggableId={carte.id} index={i} isDragDisabled={!canEdit}>
                                {(cardProvided, cardSnapshot) => (
                                  <div
                                    ref={cardProvided.innerRef}
                                    {...cardProvided.draggableProps}
                                    {...cardProvided.dragHandleProps}
                                    className={cardSnapshot.isDragging ? "carte-drag" : ""}
                                  >
                                    <ApercuCarte carte={carte} users={users} onClick={() => setCarteOuverte(carte)} />
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {dropProvided.placeholder}
                          </div>
                        )}
                      </Droppable>
                      {canEdit && (
                        ajoutCarte?.colId === col.id ? (
                          <form onSubmit={creerCarte} className="mt-2 space-y-2">
                            <Input
                              autoFocus
                              required
                              placeholder="Titre de la carte"
                              value={ajoutCarte.titre}
                              onChange={(e) => setAjoutCarte({ ...ajoutCarte, titre: e.target.value })}
                              className="rounded-xl"
                              data-testid="new-card-input"
                            />
                            <div className="flex gap-2">
                              <Button type="submit" size="sm" className="rounded-full" data-testid="new-card-submit">Ajouter</Button>
                              <Button type="button" size="sm" variant="ghost" onClick={() => setAjoutCarte(null)} className="rounded-full">Annuler</Button>
                            </div>
                          </form>
                        ) : (
                          <button
                            onClick={() => setAjoutCarte({ colId: col.id, titre: "" })}
                            className="mt-2 w-full flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm text-muted-foreground hover:bg-muted transition-colors"
                            data-testid={`add-card-button-${col.id}`}
                          >
                            <Plus className="h-4 w-4" /> Ajouter une carte
                          </button>
                        )
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              {canEdit && (
                <div className="w-64 shrink-0">
                  {colonneFormVisible ? (
                    <form onSubmit={creerColonne} className="bg-card rounded-2xl border border-border p-3 space-y-2">
                      <Input autoFocus required placeholder="Nom de la colonne" value={ajoutColonne} onChange={(e) => setAjoutColonne(e.target.value)} className="rounded-xl" data-testid="new-column-input" />
                      <div className="flex gap-2">
                        <Button type="submit" size="sm" className="rounded-full" data-testid="new-column-submit">Ajouter</Button>
                        <Button type="button" size="sm" variant="ghost" onClick={() => setColonneFormVisible(false)} className="rounded-full">Annuler</Button>
                      </div>
                    </form>
                  ) : (
                    <button
                      onClick={() => setColonneFormVisible(true)}
                      className="w-full flex items-center gap-1.5 rounded-2xl border border-dashed border-border px-4 py-3 text-sm text-muted-foreground hover:bg-card transition-colors"
                      data-testid="add-column-button"
                    >
                      <Plus className="h-4 w-4" /> Ajouter une colonne
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <CardDialog
        open={Boolean(carteOuverte)}
        onClose={() => setCarteOuverte(null)}
        carte={carteOuverte}
        users={users}
        canEdit={canEdit}
        onSaved={charger}
      />
    </div>
  );
}
